import { useCallback, useContext, useMemo } from 'react';

import type { offlineStores } from './consts';
import { UseOfflineStoreContext } from './use-offline-store.context';
import type { OfflineDataStoreScope } from '../offline-data-store';
import type {
  DefaultError,
  QueryExecuteOptions,
  QueryKey,
} from '@tanstack/react-query';

/**
 * Provides a location-scoped offline store and utilities for fetching and persisting data.
 * The store is created only when its authentication, client, and location scope is ready.
 * Until then, `scope`, `offlineStore`, and `fetchAndPersist` are undefined.
 *
 * @param storeName - The name of the offline store to access.
 * @returns The optional scope, offline store, and `fetchAndPersist` function.
 *
 * @example
 * ```ts
 * const { fetchAndPersist } = useOfflineStore('Checklist');
 *
 * if (fetchAndPersist) {
 *   await fetchAndPersist({
 *     queryOptions: {
 *       queryKey: ['someKey'],
 *       queryFn: fetchFunction,
 *     },
 *   });
 * }
 * ```
 *
 * The `offlineStore` instance can also be used directly for other offline
 * storage operations when needed.
 */
export function useOfflineStore(storeName: keyof typeof offlineStores) {
  const {
    useAuthContext,
    useQueryClient,
    useCdmSelectedLocationContext,
    createOfflineDataStore,
  } = useContext(UseOfflineStoreContext);
  const { client, authState } = useAuthContext();
  const queryClient = useQueryClient();
  const { selectedLocationConfig } = useCdmSelectedLocationContext();
  const selectedLocation = selectedLocationConfig?.dataStorage?.rootLocation;
  // cdfId is a stable, unique user ID. It only changes when the organization
  // switches identity providers, so we accept the risk that offline data stored
  // under the old ID will no longer be found, and we should communicate this to
  // customers.
  const userId = authState.user.cdfId;
  const cluster = client.getBaseUrl();
  const project = client.project;

  const { offlineStore, scope } = useMemo(() => {
    if (
      !selectedLocation?.externalId ||
      !selectedLocation.space ||
      !userId ||
      !cluster ||
      !project
    ) {
      return { offlineStore: undefined, scope: undefined };
    }

    const scope: OfflineDataStoreScope = {
      userId,
      cluster,
      project,
      location: selectedLocation.externalId,
      locationSpace: selectedLocation.space,
    };

    // TODO(FO-4396): Close the previous IndexedDB connection when replacing or releasing the store.
    return { offlineStore: createOfflineDataStore(scope, storeName), scope };
  }, [
    userId,
    cluster,
    project,
    selectedLocation?.externalId,
    selectedLocation?.space,
    storeName,
    createOfflineDataStore,
  ]);

  const persistQuery = useCallback(
    async <
      TQueryFnData,
      TError = DefaultError,
      TData = TQueryFnData,
      TQueryData = TQueryFnData,
      TQueryKey extends QueryKey = QueryKey,
      TPageParam = never,
    >({
      queryOptions,
      forceRefresh = false,
    }: {
      queryOptions: Omit<
        QueryExecuteOptions<
          TQueryFnData,
          TError,
          TData,
          TQueryData,
          TQueryKey,
          TPageParam
        >,
        'staleTime'
      >;
      forceRefresh?: boolean;
    }): Promise<void> => {
      if (!offlineStore) return;

      const data = await queryClient.query({
        ...queryOptions,
        staleTime: forceRefresh ? 0 : 'static',
      });

      await offlineStore.save(queryOptions.queryKey, data);
    },
    [offlineStore, queryClient]
  );

  // Keep persistence unavailable until the location-scoped store is ready so callers can defer the operation instead of silently dropping it.
  const fetchAndPersist = offlineStore ? persistQuery : undefined;

  return {
    offlineStore,
    scope,
    fetchAndPersist,
  };
}
